import { List, Map, fromJS } from 'immutable';
import Actions from 'flow/actions';

const initialVideoChatState = Map({
    messages: List(),
    connected: false,
    // True while the client is between a dropped side-channel socket and a
    // successful re-open (self-healing reconnect in video-chat-client.ts).
    // Distinct from `connected:false` with `reconnecting:false`, which is a
    // terminal disconnect (meeting left / never connected).
    reconnecting: false,
    errorMessage: null,
    // Mirrors the meeting chat panel's open state (VID-433).
    chatOpen: false,
    // Number of mounted useInMeetingExternalMessages instances currently rendering these
    // messages elsewhere; a count (not a flag) so one viewer closing doesn't clear it while
    // another (e.g. docked panel + wide-desktop /messages route) is still open (VID-433).
    viewingExternalMessagesCount: 0,
    // isExternal messages received while neither of the above was true (VID-433).
    unreadExternalCount: 0,
});

/**
 * Slice for the in-meeting side-channel (video-chat service).
 *
 * Distinct from the `chats` slice — that one mirrors chat-service's persistent
 * chats and is identity-gated. This slice is the live, room-scoped relay
 * consumed via WebSocket. The service is fire-and-forget — no history, no
 * presence, no persistence. Internal clients dedup against the authoritative
 * chat-service feed by reading {@code selectVideoChatExternalMessages} (only
 * {@code isExternal:true} entries); guests read all messages.
 */
export const videoChatReducer = (
    state = initialVideoChatState,
    action: any,
) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE:
        case Actions.VIDEO_CHAT_DISCONNECTED: {
            return initialVideoChatState;
        }
        case Actions.VIDEO_CHAT_CONNECTED: {
            // Preserve messages: a reconnect (self-healing or full-meeting)
            // must not wipe the guest's in-meeting history. On the very first
            // connect `messages` is already empty (initial / prior DISCONNECTED
            // reset), so this is equivalent to a fresh start there.
            return state
                .set('connected', true)
                .set('reconnecting', false)
                .set('errorMessage', null);
        }
        case Actions.VIDEO_CHAT_RECONNECTING: {
            // Side-channel socket dropped unexpectedly; the client is retrying.
            // Keep messages so the panel doesn't blank out mid-meeting.
            return state.set('connected', false).set('reconnecting', true);
        }
        case Actions.VIDEO_CHAT_MESSAGE_RECEIVED: {
            const next = state.update('messages', (list: List<any>) =>
                list.push(fromJS(action.message)),
            );
            const isUnseenGuestMessage =
                action.message?.isExternal &&
                !state.get('chatOpen') &&
                state.get('viewingExternalMessagesCount') === 0;
            return isUnseenGuestMessage
                ? next.update(
                      'unreadExternalCount',
                      (count: number) => count + 1,
                  )
                : next;
        }
        case Actions.MEETING_SET_CHAT_OPEN_STATUS: {
            return state
                .set('chatOpen', !!action.chatOpen)
                .set(
                    'unreadExternalCount',
                    action.chatOpen ? 0 : state.get('unreadExternalCount'),
                );
        }
        case Actions.VIDEO_CHAT_EXTERNAL_MESSAGES_VIEWING: {
            const nextCount = Math.max(
                0,
                state.get('viewingExternalMessagesCount') +
                    (action.viewing ? 1 : -1),
            );
            return state
                .set('viewingExternalMessagesCount', nextCount)
                .set(
                    'unreadExternalCount',
                    action.viewing ? 0 : state.get('unreadExternalCount'),
                );
        }
        case Actions.VIDEO_CHAT_ERROR_RECEIVED: {
            return state.set('errorMessage', action.reason ?? 'error');
        }
        case Actions.VIDEO_CHAT_ERROR_CLEARED: {
            return state.set('errorMessage', null);
        }
        default: {
            return state;
        }
    }
};

export default videoChatReducer;
